import React, { useState, useEffect } from 'react';
import AuthService from '../services/AuthService';
import { useNavigate, NavLink, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from "../services/ApiService";
import Counter from "../components/Counter";
import AreaChart from "../components/AreaChart";
import PieChart from "../components/PieChart";
import { formatCurrency, formatDate } from "../utils/helper";
import { getAppointmentStatus, getOrderStatus } from "../utils/statusHelper";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';


const Dashboard = () => {
    const navigate = useNavigate();
    
    const [isLoader, setIsLoader] = useState(false);
    const [stats, setStats] = useState({});
    const [recentAppointments, setRecentAppointments] = useState([]);
    const [recentSales, setRecentSales] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [graphData, setGraphData] = useState({ labels: [], patients: [], sales: [] });
    const [expenseGraphData, setExpenseGraphData] = useState({ labels: [], data: [] });
    
    const [current_month, setCurrentMonth] = useState("");
    const currency = process.env.REACT_APP_CURRENCY || 'AED';

    const authUser = AuthService.getCurrentUser();

useEffect(() => {
    const isAuthenticated = AuthService.getCurrentUser(); // Check if the user is authenticated
    if (!isAuthenticated) {
        navigate('/login'); // Redirect to dashboard if authenticated
    }
    getDashboard();

    const now = new Date();
    const formattedDate = formatDate(now, "MMM, YYYY");
    setCurrentMonth(formattedDate);
}, [navigate, selectedMonth, selectedYear]);


const getDashboard = async () => {
    try {
        setIsLoader(true);
        const response = await ApiService.request({
            method: "GET",
            url: `/dashboard?month=${selectedMonth}&year=${selectedYear}`,
        });
        const data = response.data;
        if (data.status) {
            setStats(data.data.stats);
            setRecentAppointments(data.data.recent_appointments || []);
            setRecentSales(data.data.recent_sales || []);
            setGraphData(data.data.graph_data || { labels: [], patients: [], sales: [] });
            setExpenseGraphData(data.data.expense_graph_data || { labels: [], data: [] });
        } else {
            toast.error(data.message);
        }
        setIsLoader(false);
    } catch (error) {
        setIsLoader(false);
        console.error('Dashboard error:', error);
    }
   
};


  return (
    <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
            <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                    <h1 className="page-heading d text-dark fw-bold fs-3  my-0">
                        Welcome, {authUser.name}
                         <span className="badge bg-base2 ms-3 text-uppercase">{typeof authUser.role === 'object' ? authUser.role?.name : authUser.role}</span>
                    </h1>
                    <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                        <li className="breadcrumb-item text-muted">
                            <a href="/dashboard" className="text-muted text-hover-primary"> Home </a>
                        </li>
                        <li className="breadcrumb-item">
                            <span className="bullet bg-gray-400 w-5px h-2px"></span>
                        </li>
                        <li className="breadcrumb-item text-muted"> Dashboard </li>
                    </ul>
                </div>
                <div className="d-flex gap-3">
                    {['admin', 'receptionist'].includes(authUser.role) && (
                    <div className="card border shadow h-100 py-3 px-5 hover-scale">
                        <div className="card-body p-0 d-flex align-items-center gap-3">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Create New Appointment Checkup</Tooltip>}>
                                <Link to="/appointments/create" className="btn btn-base d-flex align-items-center">
                                    <i className="fas fa-stethoscope fs-2 me-2"></i> Checkup
                                </Link>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Create New Sales Order</Tooltip>}>
                                <Link to="/orders/create" className="btn btn-base2 d-flex align-items-center">
                                    <i className="fas fa-shopping-cart fs-2 me-2 text-white"></i> Sale
                                </Link>
                            </OverlayTrigger>
                        </div>
                    </div>
                    )}
                    
                </div>
            </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
            <div id="kt_app_content_container" className="app-container container-fluid">
                {/* Row 1: Key Performance Indicators */}
                <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
                    {['admin', 'receptionist'].includes(authUser.role) && (
                    <>
                        {/* Total Appointments */}
                        <div className="col-xl-3 col-md-6 mb-4">
                            <NavLink to="/patients" className="card border shadow h-100 py-2 hover-scale bg-light-primary">
                                <div className="card-body">
                                    <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                            <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Total Patients</div>
                                            <div className="h1 mb-0 fw-bold text-primary">
                                                <Counter value={stats?.total_patients || 0} duration={1500} />
                                            </div>
                                            <small className="text-muted">Registered patients</small>
                                        </div>
                                        <div className="col-auto">
                                            <i className="fas fa-user-injured fs-3x text-primary"></i>
                                        </div>
                                    </div>
                                </div>
                            </NavLink>
                        </div>

                        {/* Total Doctors */}
                        <div className="col-xl-3 col-md-6 mb-4">
                            <NavLink to="/users?role=doctor" className="card border shadow h-100 py-2 hover-scale bg-light-info">
                                <div className="card-body">
                                    <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                            <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Total Doctors</div>
                                            <div className="h1 mb-0 fw-bold text-info">
                                                <Counter value={stats?.total_doctors || 0} duration={1500} />
                                            </div>
                                            <small className="text-muted">Active doctors</small>
                                        </div>
                                        <div className="col-auto">
                                            <i className="fas fa-user-md fs-3x text-info"></i>
                                        </div>
                                    </div>
                                </div>
                            </NavLink>
                        </div>

                        {/* Total Receptionists */}
                        <div className="col-xl-3 col-md-6 mb-4">
                            <NavLink to="/users?role=receptionist" className="card border shadow h-100 py-2 hover-scale bg-light-warning">
                                <div className="card-body">
                                    <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                            <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Receptionists</div>
                                            <div className="h1 mb-0 fw-bold text-warning">
                                                <Counter value={stats?.total_receptionists || 0} duration={1500} />
                                            </div>
                                            <small className="text-muted">Staff members</small>
                                        </div>
                                        <div className="col-auto">
                                            <i className="fas fa-users fs-3x text-warning"></i>
                                        </div>
                                    </div>
                                </div>
                            </NavLink>
                        </div>
                    </>
                    )}

                    {/* Total and Upcimg Appointments */}
                    <div className="col-xl-3 col-md-6 mb-4">
                        <NavLink to="/appointments?status=upcoming" className="card border shadow h-100 py-2 hover-scale bg-light-primary">
                            <div className="card-body">
                                <div className="row no-gutters align-items-center">
                                    <div className="col mr-2">
                                        <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Upcoming Appointments</div>
                                        <div className="h1 mb-0 fw-bold text-primary">
                                            <Counter value={stats?.total_upcoming_appointments || 0} duration={1500} />
                                        </div>
                                        <small className="text-muted">Upcoming appointments</small>
                                    </div>
                                    <div className="col-auto">
                                        <i className="fas fa-calendar-alt fs-3x text-primary"></i>
                                    </div>
                                </div>
                            </div>
                        </NavLink>
                    </div>
                    <div className="col-xl-3 col-md-6 mb-4">
                        <NavLink to="/appointments" className="card border shadow h-100 py-2 hover-scale bg-light-success">
                            <div className="card-body">
                                <div className="row no-gutters align-items-center">
                                    <div className="col mr-2">
                                        <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Appointments</div>
                                        <div className="h1 mb-0 fw-bold text-success">
                                            <Counter value={stats?.total_appointments || 0} duration={1500} />
                                        </div>
                                        <small className="text-muted">Total bookings</small>
                                    </div>
                                    <div className="col-auto">
                                        <i className="fas fa-calendar-check fs-3x text-success"></i>
                                    </div>
                                </div>
                            </div>
                        </NavLink>
                    </div>
                    {/* Row 2: Sales & Products Stats */}
                    {['admin', 'receptionist'].includes(authUser.role) && (
                        <>
                            {/* Total Products */}
                            <div className="col-xl-3 col-md-6 mb-4">
                                <NavLink to="/products" className="card border shadow h-100 py-2 hover-scale bg-light-info">
                                    <div className="card-body">
                                        <div className="row no-gutters align-items-center">
                                            <div className="col mr-2">
                                                <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Total Products</div>
                                                <div className="h1 mb-0 fw-bold text-info">
                                                    <Counter value={stats?.total_products || 0} duration={1500} />
                                                </div>
                                                <small className="text-muted">In inventory</small>
                                            </div>
                                            <div className="col-auto">
                                                <i className="fas fa-box fs-3x text-info"></i>
                                            </div>
                                        </div>
                                    </div>
                                </NavLink>
                            </div>

                            {/* Out of Stock */}
                            <div className="col-xl-3 col-md-6 mb-4">
                                <NavLink to="/products" className="card border shadow h-100 py-2 hover-scale bg-light-danger">
                                    <div className="card-body">
                                        <div className="row no-gutters align-items-center">
                                            <div className="col mr-2">
                                                <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Out of Stock</div>
                                                <div className="h1 mb-0 fw-bold text-danger">
                                                    <Counter value={stats?.out_of_stock_products || 0} duration={1500} />
                                                </div>
                                                <small className="text-muted">Products needs attention</small>
                                            </div>
                                            <div className="col-auto">
                                                <i className="fas fa-exclamation-triangle fs-3x text-danger"></i>
                                            </div>
                                        </div>
                                    </div>
                                </NavLink>
                            </div>
                            {/* Total Sale */}
                            {['admin'].includes(authUser.role) && (
                            <>
                                <div className="col-xl-3 col-md-6 mb-4">
                                <NavLink to="/orders" className="card border shadow h-100 py-2 hover-scale bg-light-success">
                                    <div className="card-body">
                                        <div className="row no-gutters align-items-center">
                                            <div className="col mr-2">
                                                <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">Total Sale</div>
                                                <div className="h1 mb-0 fw-bold text-success">
                                                    {currency} <Counter value={stats?.total_sale || 0} duration={1500} />
                                                </div>
                                                <small className="text-muted">Lifetime revenue</small>
                                            </div>
                                            <div className="col-auto">
                                                <i className="fas fa-money-bill fs-3x text-success"></i>
                                            </div>
                                        </div>
                                    </div>
                                </NavLink>
                                </div>
                                {/* This Month Sale */}
                                <div className="col-xl-3 col-md-6 mb-4">
                                    <NavLink to="/orders" className="card border shadow h-100 py-2 hover-scale bg-light-warning">
                                        <div className="card-body">
                                            <div className="row no-gutters align-items-center">
                                                <div className="col mr-2">
                                                    <div className="fs-6 fw-bold text-gray-900 text-uppercase mb-2">This Month Sale</div>
                                                    <div className="h1 mb-0 fw-bold text-warning">
                                                        {currency} <Counter value={stats?.this_month_sale || 0} duration={1500} />
                                                    </div>
                                                    <small className="text-muted">{current_month}</small>
                                                </div>
                                                <div className="col-auto">
                                                    <i className="fas fa-chart-line fs-3x text-warning"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </NavLink>
                                </div>
                            </>

                            )}

                        </>
                    )}

                </div>

                {/* Graphs Section */}
                <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
                    <div className="col-xl-8 col-12">
                        <div className="card border shadow h-100">
                            <div className="card-header border-0 pt-5">
                                <h3 className="card-title align-items-start flex-column">
                                    <span className="card-label fw-bold fs-3 mb-1">Performance Overview</span>
                                    <span className="text-muted mt-1 fw-semibold fs-7">Daily Patients & Sales</span>
                                </h3>
                                <div className="card-toolbar">
                                    <div className="d-flex gap-2">
                                        <select 
                                            className="form-select form-select-sm form-select-solid" 
                                            value={selectedMonth}
                                            onChange={(e) => setSelectedMonth(Number(e.target.value))}
                                        >
                                            {Array.from({ length: 12 }, (_, i) => (
                                                <option key={i + 1} value={i + 1}>
                                                    {new Date(0, i).toLocaleString('default', { month: 'long' })}
                                                </option>
                                            ))}
                                        </select>
                                        <select 
                                            className="form-select form-select-sm form-select-solid"
                                            value={selectedYear}
                                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                                        >
                                            {Array.from({ length: 5 }, (_, i) => (
                                                <option key={i} value={new Date().getFullYear() - 2 + i}>
                                                    {new Date().getFullYear() - 2 + i}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body py-3">
                                <div className="row">
                                    <div className="col-md-6 mb-4">
                                        <div className="border rounded p-3">
                                            <h4 className="text-center mb-3">Patients Seen</h4>
                                            <AreaChart 
                                                labels={graphData.labels} 
                                                label="Patients" 
                                                dataset={graphData.patients} 
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="border rounded p-3">
                                            <h4 className="text-center mb-3">Sales / Income</h4>
                                            <AreaChart 
                                                labels={graphData.labels} 
                                                label="Sales" 
                                                dataset={graphData.sales} 
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-4 col-12">
                        <div className="card border shadow h-100">
                             <div className="card-header border-0 pt-5">
                                <h3 className="card-title align-items-start flex-column">
                                    <span className="card-label fw-bold fs-3 mb-1">Expenses</span>
                                    <span className="text-muted mt-1 fw-semibold fs-7">By Category</span>
                                </h3>
                             </div>
                             <div className="card-body d-flex align-items-center justify-content-center">
                                 <div style={{ height: '300px', width: '100%' }}>
                                    {expenseGraphData.data.length > 0 ? (
                                        <PieChart labels={expenseGraphData.labels} data={expenseGraphData.data} />
                                    ) : (
                                        <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                                            No expense data for this month
                                        </div>
                                    )}
                                 </div>
                             </div>
                        </div>
                    </div>
                </div>
                
                

                {/* Recent Tables */}
                <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
                    {/* Recent Appointments */}
                    <div className="col-xl-6 mb-4">
                        <div className="card border shadow h-100">
                            <div className="card-header border-0 pt-5">
                                <h3 className="card-title align-items-start flex-column">
                                    <span className="card-label fw-bold fs-3 mb-1">Recent Appointments</span>
                                    <span className="text-muted mt-1 fw-semibold fs-7">Latest 5 bookings</span>
                                </h3>
                                <div className="card-toolbar">
                                    <Link to="/appointments" className="btn btn-sm btn-light-primary">View All</Link>
                                </div>
                            </div>
                            <div className="card-body py-3">
                                <div className="table-responsive">
                                    <table className="table align-middle gs-0 gy-4">
                                        <thead>
                                            <tr className="fw-bold text-muted bg-light">
                                                <th className="ps-4 min-w-100px rounded-start">Patient</th>
                                                <th className="min-w-100px">Doctor</th>
                                                <th className="min-w-100px">Date/Time</th>
                                                <th className="min-w-50px rounded-end">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentAppointments.map((appt, index) => (
                                                <tr key={index}>
                                                    <td className="ps-4">
                                                        <div className="d-flex align-items-center">
                                                            <div className="d-flex justify-content-start flex-column">
                                                                <span className="text-dark fw-bold text-hover-primary mb-1 fs-6">{appt.patient?.name}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted fw-semibold d-block fs-7">{appt.doctor?.name}</span>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted fw-semibold d-block fs-7">{formatDate(appt.date,'MMM DD, YYYY')}</span>
                                                        <span className="text-muted fw-semibold d-block fs-7">{appt.time}</span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${getAppointmentStatus(appt.status).badgeClass} fs-7 fw-bold`}>{getAppointmentStatus(appt.status).label}</span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {recentAppointments.length === 0 && (
                                                <tr>
                                                    <td colSpan="4" className="text-center text-muted">No appointments found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recent Sales */}
                    {['admin', 'receptionist'].includes(authUser.role) && (
                        <div className="col-xl-6 mb-4">
                            <div className="card border shadow h-100">
                                <div className="card-header border-0 pt-5">
                                    <h3 className="card-title align-items-start flex-column">
                                        <span className="card-label fw-bold fs-3 mb-1">Recent Sales</span>
                                        <span className="text-muted mt-1 fw-semibold fs-7">Latest 5 orders</span>
                                    </h3>
                                    <div className="card-toolbar">
                                        <Link to="/orders" className="btn btn-sm btn-light-primary">View All</Link>
                                    </div>
                                </div>
                                <div className="card-body py-3">
                                    <div className="table-responsive">
                                        <table className="table align-middle gs-0 gy-4">
                                            <thead>
                                                <tr className="fw-bold text-muted bg-light">
                                                    <th className="ps-4 min-w-100px rounded-start">Order ID</th>
                                                    <th className="min-w-100px">Customer</th>
                                                    <th className="min-w-100px">Amount</th>
                                                    <th className="min-w-50px rounded-end">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recentSales.map((sale, index) => (
                                                    <tr key={index}>
                                                        <td className="ps-4">
                                                            <span className="text-dark fw-bold text-hover-primary mb-1 fs-6">#{sale.id}</span>
                                                        </td>
                                                        <td>
                                                            <span className="text-muted fw-semibold d-block fs-7">{sale.customer_name}</span>
                                                        </td>
                                                        <td>
                                                            <span className="text-success fw-bold d-block fs-7">{formatCurrency(sale.total_amount)}</span>
                                                        </td>
                                                        <td>
                                                            {(() => {
                                                                const { badgeClass, label } = getOrderStatus(sale.status);
                                                                return (
                                                                    <span className={`badge ${badgeClass} fs-7 fw-bold`}>{label}</span>
                                                                );
                                                            })()}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {recentSales.length === 0 && (
                                                    <tr>
                                                        <td colSpan="4" className="text-center text-muted">No sales found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    </div>
  );
};

export default Dashboard;
